import { RefObject, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@store';
import {
  Filters,
  PermissionEnum,
  SchemaFilterType,
  useFilters,
  useGetList,
  useQueryState,
  useScroll,
} from '@ui/src';
import {
  useEndCustomerSessionMutation,
  useLazyGetCustomerDevicesQuery,
} from '../../store/services/individual';
import { filtersSchema, schema } from '../variables/devices';
import { Device } from '../../types';
import { setTakeCount } from '../../store/slices/devices';
import formatDevicesList from '../helpers/format-devices-list';

const useDevicesList = ({
  customerId,
  devices,
  activePermissions,
  scrollRef,
  takeCount,
}: {
  customerId?: string;
  devices?: Device[];
  activePermissions?: PermissionEnum[];
  scrollRef: RefObject<HTMLDivElement | null>;
  takeCount?: number;
}) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState('');

  const dispatch = useAppDispatch();

  const [getList, { isFetching, isLoading }] = useLazyGetCustomerDevicesQuery();
  const [
    endSessions,
    { isLoading: endSessionInProgress, isSuccess: endSessionsIsSuccess },
  ] = useEndCustomerSessionMutation();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const { getListAction, setPage, onFilter, errorList } = useGetList<Device>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
    itemId: customerId,
  });
  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const onEndSessions = (id: string) => {
    setSelectedDeviceId(id);

    endSessions({
      id: customerId as string,
      deviceId: id,
    });
  };

  const formattedData = useMemo(() => {
    const data = formatDevicesList(
      devices as Device[],
      activePermissions as PermissionEnum[],
      onEndSessions,
      endSessionInProgress,
      selectedDeviceId,
    );

    return data;
  }, [devices, sort, filters, activePermissions, endSessionInProgress]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      filters &&
      !devices &&
      takeCount &&
      takeCount !== 0 &&
      activePermissions?.includes(PermissionEnum.EMPLOYEES_DEVICES_LIST)
    ) {
      const nextFilters: Filters = {
        ...filters,
        take: {
          value: [takeCount?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: nextFilters,
      });
    }
  }, [filters, devices, takeCount, activePermissions]);

  useEffect(() => {
    if (endSessionsIsSuccess) {
      setSelectedDeviceId('');
    }
  }, [endSessionsIsSuccess]);

  useEffect(
    () => () => {
      setSelectedDeviceId('');
    },
    [],
  );

  return {
    formattedData,
    filters,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    errorList,
    setPage,
  };
};

export default useDevicesList;
